'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useTenantAndVariant } from '@multitenancy/TenantAndVariantContext';

export function HeroMinimal() {
  const { storeConfig, featureToggles } = useTenantAndVariant();
  const heroImage = storeConfig.heroImage || '/images/hero.png';
  const isFocused = featureToggles.heroWidthMode === 'focused';
  const isLimited = featureToggles.heroWidthMode === 'limited' || isFocused;

  return (
    <div className={`relative w-full ${isFocused ? 'bg-black' : ''}`}>
      {/* Blurred background for focused mode */}
      {isFocused && (
        <div className="absolute inset-0 overflow-hidden">
          <Image
            src={heroImage}
            alt="Background"
            fill
            className="object-cover blur-2xl scale-110 opacity-60"
            priority
          />
        </div>
      )}

      <section className={`relative h-screen w-full flex items-center overflow-hidden bg-black ${isLimited ? 'max-w-[1920px] mx-auto' : ''}`}>
        {/* Background Image with dark overlay */}
        <div className="absolute inset-0">
          <Image
            src={heroImage}
            alt="SPECIAL SALE"
            fill
            className="object-cover opacity-40"
            priority
          />
          {/* Strong dark overlay for text contrast */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50" />
        </div>

        {/* Animated accent lines */}
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#8B3A3A] via-[#D4B896] to-[#8B3A3A] animate-shimmer" />
        <div className="absolute bottom-0 left-0 w-full h-2 bg-gradient-to-r from-[#8B3A3A] via-[#D4B896] to-[#8B3A3A] animate-shimmer-delayed" />

        {/* Main Content - Left Aligned */}
        <div className="relative z-10 w-full px-8 md:px-16 lg:px-24 max-w-6xl">
          {/* Flash sale badge */}
          <div className="inline-flex items-center gap-3 mb-8 animate-fade-in-down">
            <div className="px-6 py-2 bg-[#8B3A3A] text-white text-sm font-bold tracking-widest uppercase border-2 border-[#D4B896]">
              ⚡ LIMITED TIME OFFER
            </div>
          </div>

          {/* Main Heading - HUGE and Bold */}
          <h1 className="text-6xl sm:text-6xl md:text-8xl lg:text-6xl xl:text-[10rem] font-bold mb-2 text-white animate-fade-in-up leading-none" style={{
            textShadow: '0 0 80px rgba(139,58,58,0.8), 0 20px 60px rgba(0,0,0,0.9)',
            letterSpacing: '0.02em'
          }}>
            SPECIAL
          </h1>

          <h1 className="text-6xl sm:text-6xl md:text-8xl lg:text-6xl xl:text-[10rem] font-bold mb-6 text-[#D4B896] animate-fade-in-up-delayed leading-none" style={{
            textShadow: '0 0 80px rgba(212,184,150,0.6), 0 20px 60px rgba(0,0,0,0.9)',
            letterSpacing: '0.02em'
          }}>
            SALE
          </h1>

          {/* Discount Banner */}
          <div className="mb-10 animate-fade-in-up-more-delayed">
            <div className="inline-block bg-gradient-to-r from-[#8B3A3A] to-[#F5E6D3] p-1">
              <div className="bg-black px-8 py-6">
                <p className="text-5xl sm:text-6xl md:text-6xl lg:text-8xl font-black text-[#D4B896]" style={{
                  textShadow: '0 4px 30px rgba(212,184,150,0.5)',
                  letterSpacing: '0.05em'
                }}>
                  UP TO 70% OFF
                </p>
              </div>
            </div>
          </div>

          {/* Subtitle - Bigger text, not centered */}
          <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light text-white/90 mb-12 max-w-3xl animate-fade-in-up-most-delayed" style={{
            textShadow: '0 4px 40px rgba(0,0,0,0.9)',
            letterSpacing: '0.15em',
            lineHeight: '1.5'
          }}>
            EXCLUSIVE DEALS ON<br />
            PREMIUM PRODUCTS
          </p>

          {/* Countdown Timer Effect */}
          <div className="flex gap-4 mb-12 animate-fade-in-final">
            <div className="text-center">
              <div className="bg-[#8B3A3A]/90 backdrop-blur-sm border-2 border-[#D4B896] px-6 py-4 min-w-[100px]">
                <p className="text-4xl md:text-5xl font-black text-white">23</p>
                <p className="text-xs md:text-sm text-[#D4B896] font-bold tracking-widest uppercase mt-1">Hours</p>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-[#8B3A3A]/90 backdrop-blur-sm border-2 border-[#D4B896] px-6 py-4 min-w-[100px]">
                <p className="text-4xl md:text-5xl font-black text-white">45</p>
                <p className="text-xs md:text-sm text-[#D4B896] font-bold tracking-widest uppercase mt-1">Minutes</p>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-[#8B3A3A]/90 backdrop-blur-sm border-2 border-[#D4B896] px-6 py-4 min-w-[100px]">
                <p className="text-4xl md:text-5xl font-black text-white">32</p>
                <p className="text-xs md:text-sm text-[#D4B896] font-bold tracking-widest uppercase mt-1">Seconds</p>
              </div>
            </div>
          </div>

          {/* CTA Buttons - Big and Bold */}
          <div className="flex flex-col sm:flex-row gap-6 animate-fade-in-final">
            <Link
              href="/store"
              className="group relative overflow-hidden"
            >
              <span className="relative z-10 inline-flex items-center gap-4 bg-[#D4B896] text-black px-12 py-6 text-base md:text-lg font-black tracking-widest uppercase transition-all duration-500 hover:bg-[#F5E6D3] hover:scale-105 hover:shadow-[0_0_50px_rgba(212,184,150,0.6)]" style={{
                letterSpacing: '0.2em'
              }}>
                SHOP SALE NOW
                <span className="text-xl">→</span>
              </span>
            </Link>
            <Link
              href="/store"
              className="group relative overflow-hidden"
            >
              <span className="relative z-10 inline-flex items-center gap-4 bg-white/10 backdrop-blur-md border-3 border-[#D4B896] text-white px-12 py-6 text-base md:text-lg font-black tracking-widest uppercase transition-all duration-500 hover:bg-[#D4B896] hover:text-black hover:scale-105" style={{
                letterSpacing: '0.2em'
              }}>
                VIEW NEW ARRIVALS
              </span>
            </Link>
          </div>

          {/* Additional Info */}
          <p className="text-sm md:text-base text-white/60 mt-8 tracking-wider animate-fade-in-final">
            * Offer valid while supplies last. Terms and conditions apply.
          </p>
        </div>

        {/* Floating sale badges */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-[#8B3A3A]/80 backdrop-blur-sm border-2 border-[#D4B896] rotate-12 items-center justify-center animate-pulse hidden lg:flex">
          <div className="text-center">
            <p className="text-4xl font-black text-white">70%</p>
            <p className="text-xs text-[#D4B896] font-bold uppercase">OFF</p>
          </div>
        </div>
        <div className="absolute bottom-32 right-32 w-24 h-24 bg-[#D4B896]/90 backdrop-blur-sm border-2 border-[#8B3A3A] -rotate-12 items-center justify-center animate-pulse hidden lg:flex" style={{ animationDelay: '1s' }}>
          <div className="text-center">
            <p className="text-3xl font-black text-black">50%</p>
            <p className="text-xs text-[#8B3A3A] font-bold uppercase">OFF</p>
          </div>
        </div>
      </section>
    </div>
  );
}
